/**
 * PURPOSE: Mostrar una tarjeta administrativa de partido con equipos, marcador y accesos operativos.
 * RESPONSIBILITIES: Renderizar estados, resultados responsivos, penales, estadísticas y accesos a planilla o marcador simple.
 * COLLABORATORS: ResultadosArea.jsx, FullScreenSimpleScore.jsx, stateMatch.js y Bandera2.jsx.
 * ROUTE: winscore/src/area/ResultadosArea/MatchCard.jsx
 */

import React, { memo, useCallback } from "react";
import Bandera2 from "../../components/Bandera/Bandera2.jsx";
import { PiSoccerBallBold } from "react-icons/pi";
import { TbCardsFilled } from "react-icons/tb";
import { MdOutlineScoreboard } from "react-icons/md";
import { BsFillFileEarmarkSpreadsheetFill } from "react-icons/bs";
import { HANDBALL_BEACH_SHOW_END_MATCH_BUTTON } from "../../components/FullScreenModal/FullScreenPlanilla/HandballBeach/constants/handballBeachEventTypes.js";

import { stateMatch } from "../../utils/stateMatch.js";
import { getParticipantLabel } from "../../utils/participantDisplayName.js";

/**
 * Tarjeta de partido. Se vuelve a pintar SOLO si cambia `match` o alguna prop primitiva.
 */
function MatchCard({
    match,
    moodName,
    useBandera,
    formattedSport,
    onOpenResultados,
    onOpenStatsFut,
    onOpenStatsVoley,
    onOpenSimpleScore,
    onOpenPlanillaVivo
}) {
    /* ====== Datos derivados (memo gratis dentro del render) ====== */
    const inst1 = match.Participant1?.Institution ?? {};
    const inst2 = match.Participant2?.Institution ?? {};

    // FTR-0006: moodName 1 => abrev; moodName 2 => participant.displayName || Institution.name
    const name1 = getParticipantLabel(match.Participant1, moodName);
    const name2 = getParticipantLabel(match.Participant2, moodName);

    const img1 = `${inst1.path_base ?? ""}${inst1.image_path ?? ""}`;
    const img2 = `${inst2.path_base ?? ""}${inst2.image_path ?? ""}`;

    const mState = stateMatch(match.state);
    const isHandballBeach = Number(formattedSport) === 7;
    const isClosedMatch = [3, 4].includes(Number(match.state));
    const canEditMatch =
        !isHandballBeach ||
        !HANDBALL_BEACH_SHOW_END_MATCH_BUTTON ||
        !isClosedMatch;

    /* ====== Handlers estables ====== */
    const handleMain = useCallback(() => {
        if (!canEditMatch) return;
        onOpenResultados(match);
    }, [canEditMatch, match, onOpenResultados]);
    const handlePlanillaVivo = useCallback(
        (e) => {
            e.stopPropagation();
            onOpenPlanillaVivo(match);
        },
        [match, onOpenPlanillaVivo],
    );
    const handleSimpleScore = useCallback(
        (e) => {
            e.stopPropagation();
            onOpenSimpleScore(match);
        },
        [match, onOpenSimpleScore],
    );
    const handleStatsFut = useCallback(
        (e, tipo) => {
            e.stopPropagation();
            onOpenStatsFut(match, tipo);
        },
        [match, onOpenStatsFut],
    );
    const handleStatsVoley = useCallback(
        (e, tipo) => {
            e.stopPropagation();
            onOpenStatsVoley(match, tipo);
        },
        [match, onOpenStatsVoley],
    );

    /* ====== JSX ====== */
    return (
        <div
            className={`card bg-white p-6 rounded-lg shadow-lg flex flex-col items-center relative pb-10 ${
                canEditMatch ? "cursor-pointer" : "cursor-default"
            }`}
            onClick={handleMain}
            title={canEditMatch ? undefined : "Edicion manual deshabilitada para Handball Beach finalizado"}
        >
            <div
                className={`not_disputed_dot ${match.flagWO !== null ? "bg-gray-800 text-white" : mState.color
                    } rounded-full absolute top-2 left-2`}
                style={{ fontSize: "0.7rem", padding: "1px 6px" }}
            >
                {match.flagWO === null ? mState.tag : match.flagWO === 999 ? "Doble W.O." : "W.O."}
            </div>

            {/* ===== Botones de estadística ===== */}
            <div className="absolute top-2 right-3 flex gap-1">
                {(formattedSport === 1 || formattedSport === 2) && (
                    <>
                        <PiSoccerBallBold
                            className="rotate-icon"
                            style={{ fontSize: "1.65rem", color: match.stateGoleadores ? "green" : "#1E88E5" }}
                            onClick={(e) => handleStatsFut(e, "goleadores")}
                        />
                        <TbCardsFilled
                            className="rotate-icon"
                            style={{ fontSize: "1.65rem", color: match.stateSanciones ? "green" : "#1E88E5" }}
                            onClick={(e) => handleStatsFut(e, "sanciones")}
                        />
                    </>
                )}
                {(formattedSport === 3 || formattedSport === 6) && (
                    <TbCardsFilled
                        className="rotate-icon"
                        style={{ fontSize: "1.65rem", color: match.stateSanciones ? "green" : "#1E88E5" }}
                        onClick={(e) => handleStatsVoley(e, "sanciones")}
                    />
                )}
            </div>

            {/* ===== Cabecera etiqueta grupo / etapa ===== */}
            {match.tag_extra && (
                <span className="absolute bottom-2 left-2 bg-indigo-600 text-white text-xs font-semibold uppercase px-2 py-0.5 rounded-md shadow">
                    {match.tag_extra.toUpperCase()}
                </span>
            )}
            <div className="text-center mb-4">
                <span className={`relative inline-block px-3 py-1 rounded-lg font-bold uppercase ${match.flag_tag_resaltante
                    ? 'bg-red-600 text-white before:content-[""] before:absolute before:inset-0 before:rounded-lg before:border-4 before:border-red-400 before:animate-ping'
                    : 'bg-gray-200 text-gray-800'
                    }`}>
                    {match.tag_final || `GRUPO ${String.fromCharCode(64 + match.idgrupo)}`}
                </span>

                {match.description_match && (
                    <span className="block mt-1 text-xs px-1 py-0.5 bg-gray-100 text-gray-600 rounded-md shadow-sm">
                        {match.description_match}
                    </span>
                )}
            </div>

            {/* ===== Contenido principal (equipos + score) ===== */}
            <div className="flex justify-around items-center mb-4 relative w-full">
                {/* Equipo 1 */}
                <TeamIcon
                    name={name1}
                    img={img1}
                    bandera={inst1.country?.img_path}
                    useBandera={useBandera}
                />

                {/* Score */}
                {[3, 7].includes(Number(formattedSport)) ? (
                    <ScoreBySets match={match} />
                ) : (
                    <ScoreDefault match={match} formattedSport={formattedSport} />
                )}

                {/* Equipo 2 */}
                <TeamIcon
                    name={name2}
                    img={img2}
                    bandera={inst2.country?.img_path}
                    useBandera={useBandera}
                />
            </div>

            {/* ===== Marcador móvil simple ===== */}
            {(Number(formattedSport) === 1 ||
                (Number(formattedSport) === 3 && ![44, 45].includes(Number(match.idsport)))) && (
                <MdOutlineScoreboard
                    className="rotate-icon absolute bottom-2 right-3"
                    style={{ fontSize: "1.75rem", color: "#1E88E5" }}
                    onClick={handleSimpleScore}
                    title="Abrir marcador"
                />
            )}

            {/* ===== Planilla Vivo ===== */}
            <BsFillFileEarmarkSpreadsheetFill
                className="rotate-icon absolute bottom-2 left-3"
                style={{ fontSize: "1.65rem", color: "#1E88E5" }}
                onClick={handlePlanillaVivo}
            />
        </div>
    );
}

/* ---------- Sub-componentes internos ---------- */
const TeamIcon = ({ img, name, bandera, useBandera }) => (
    <div className="team_icon w-16 h-16 rounded-full flex flex-col items-center relative">
        <div className="circle w-12 h-12 rounded-full flex items-center justify-center mb-2">
            {img ? (
                <>
                    <img src={img} alt={name || "Por Definir"} className="w-12 h-12" loading="lazy" />
                    {useBandera && <Bandera2 src={bandera || ""} alt="bandera" />}
                </>
            ) : (
                <div className="w-12 h-12 bg-gray-300 rounded-full" />
            )}
        </div>
        <div className="text-xs font-semibold text-gray-900 text-center w-20">{name || "Por Definir"}</div>
    </div>
);

const ScoreDefault = ({ match, formattedSport }) => (
    <div className="mx-0 grid shrink-0 grid-cols-[auto_auto_auto] items-baseline gap-1 sm:mx-2 sm:gap-2">
        <div className="score inline-flex items-baseline gap-0.5 whitespace-nowrap text-2xl font-bold tabular-nums text-gray-900 sm:text-3xl">
            <span className="text-xs sm:text-sm">
                {(formattedSport === 1 || formattedSport === 2 || formattedSport === 5) && (match.defpenal1 > 0 || match.defpenal2 > 0)
                    ? ` (${match.defpenal1})`
                    : ""}
            </span>
            {match.resultado1}
        </div>
        <span className="shrink-0 text-base font-bold sm:text-xl">vs</span>
        <div className="score inline-flex items-baseline gap-0.5 whitespace-nowrap text-2xl font-bold tabular-nums text-gray-900 sm:text-3xl">
            {match.resultado2}
            <span className="text-xs sm:text-sm">
                {(formattedSport === 1 || formattedSport === 2 || formattedSport === 5) && (match.defpenal2 > 0 || match.defpenal1 > 0)
                    ? ` (${match.defpenal2})`
                    : ""}
            </span>
        </div>
    </div>
);

function getPeriodPoints(period, side) {
    const candidates =
        side === 1
            ? [period?.puntos1, period?.resultado1, period?.score1, period?.points1, period?.t1]
            : [period?.puntos2, period?.resultado2, period?.score2, period?.points2, period?.t2];

    const value = candidates.find((item) => item !== undefined && item !== null && item !== "");
    return Number.isFinite(Number(value)) ? Number(value) : null;
}

function resolveSetsWon(match, side) {
    const direct = side === 1 ? match.sets1 : match.sets2;
    if (direct !== undefined && direct !== null && direct !== "") return Number(direct) || 0;

    return (match.periods || []).reduce((acc, period) => {
        const p1 = getPeriodPoints(period, 1);
        const p2 = getPeriodPoints(period, 2);
        if (p1 == null || p2 == null || p1 === p2) return acc;
        return acc + (side === 1 ? Number(p1 > p2) : Number(p2 > p1));
    }, 0);
}

const ScoreBySets = ({ match }) => {
    const sets1 = resolveSetsWon(match, 1);
    const sets2 = resolveSetsWon(match, 2);

    const visiblePeriods = (match.periods || []).filter((period) => {
        const p1 = getPeriodPoints(period, 1);
        const p2 = getPeriodPoints(period, 2);
        return p1 != null || p2 != null;
    });

    return (
        <>
            <div className="score text-3xl font-bold mx-4 text-gray-900 flex">
                <div className="text-center mr-1">
                    {visiblePeriods.map((p, i) => (
                        <div key={i} className="text-xs text-gray-500">
                            {getPeriodPoints(p, 1)}
                        </div>
                    ))}
                </div>
                {sets1}
            </div>
            <span className="text-xl font-bold">vs</span>
            <div className="score text-3xl font-bold mx-4 text-gray-900 flex">
                {sets2}
                <div className="text-center ml-1">
                    {visiblePeriods.map((p, i) => (
                        <div key={i} className="text-xs text-gray-500">
                            {getPeriodPoints(p, 2)}
                        </div>
                    ))}
                </div>
            </div>
        </>
    );
};

export default memo(MatchCard);
